/**
 * Cookbook context — the active-cookbook state for the app shell (the
 * "context-switcher" the architecture doc §3.7 asks for).
 *
 * It holds which of the caller's cookbooks is currently active and exposes a
 * setter. The list + the caller's per-cookbook ROLE come straight from
 * `GET /me` (the server is authoritative). The role is a UI hint only — every
 * write is independently enforced by the API and Postgres RLS, so a tampered
 * client can never exceed its real permissions.
 *
 * Active-cookbook selection:
 *  - Default = the cookbook the user OWNS (their primary), not just whichever
 *    one /me happens to return first.
 *  - A deliberate switch is remembered across refresh / next login on the same
 *    device via localStorage, keyed per user (`subject`). Only a cookbook id is
 *    stored — not sensitive (the user already sees it; access stays server-side)
 *    — so this is NOT the token "never persist" rule (that is about credentials).
 *  - A stored id that is no longer in the user's cookbooks (e.g. access revoked,
 *    or a different user on the same device) is ignored → falls back to owned.
 */

import type { CookbookSummary } from "@tjommi/shared";
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

interface CookbookContextValue {
  /** All cookbooks the caller belongs to (from /me). Always non-empty here. */
  cookbooks: CookbookSummary[];
  /** The currently active cookbook (drives the feed + switcher label). */
  active: CookbookSummary;
  /** Switch the active cookbook by id (no-op if the id is unknown). */
  setActiveId: (id: string) => void;
}

const CookbookContext = createContext<CookbookContextValue | null>(null);

/** The user's owned (primary) cookbook, or the first one as a fallback. */
function ownedOrFirst(cookbooks: CookbookSummary[]): CookbookSummary | undefined {
  return cookbooks.find((c) => c.role === "owner") ?? cookbooks[0];
}

function storageKeyFor(userId: string | undefined): string | null {
  return userId ? `desjeff.activeCookbook.${userId}` : null;
}

function readStored(storageKey: string | null, cookbooks: CookbookSummary[]): string | null {
  if (!storageKey) return null;
  try {
    const stored = localStorage.getItem(storageKey);
    // Only honour it if the user still has access to that cookbook.
    return stored && cookbooks.some((c) => c.id === stored) ? stored : null;
  } catch {
    return null;
  }
}

export function CookbookProvider({
  cookbooks,
  userId,
  children,
}: {
  cookbooks: CookbookSummary[];
  /** The caller's subject — scopes the remembered selection per user/device. */
  userId?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const storageKey = storageKeyFor(userId);

  const [activeId, setActiveIdState] = useState<string>(
    () => readStored(storageKey, cookbooks) ?? ownedOrFirst(cookbooks)?.id ?? "",
  );

  const setActiveId = useCallback(
    (id: string) => {
      setActiveIdState(id);
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, id);
        } catch {
          // Persisting is best-effort; selection still works for this session.
        }
      }
    },
    [storageKey],
  );

  const value = useMemo<CookbookContextValue>(() => {
    const active = cookbooks.find((c) => c.id === activeId) ?? ownedOrFirst(cookbooks)!;
    return { cookbooks, active, setActiveId };
  }, [cookbooks, activeId, setActiveId]);

  return <CookbookContext.Provider value={value}>{children}</CookbookContext.Provider>;
}

export function useCookbook(): CookbookContextValue {
  const ctx = useContext(CookbookContext);
  if (!ctx) {
    throw new Error("useCookbook must be used within a CookbookProvider");
  }
  return ctx;
}
